import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, ArrowLeft, CalendarDays, Users, Copy, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, brl, formatApiError, STAFF_ROLES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import FloatingMenuButton from "@/components/FloatingMenuButton";
import TournamentBoard from "@/components/TournamentBoard";
import { Podium, RankList } from "@/components/Podium";

const STATUS = { inscricoes: "Inscrições abertas", em_andamento: "Em andamento", finalizado: "Finalizado" };

function RegisterBox({ t, cat, onDone, pix }) {
  const { user } = useAuth();
  const [partners, setPartners] = useState([]);
  const [pid, setPid] = useState("");
  const [pname, setPname] = useState("");
  const [paid, setPaid] = useState(null);
  useEffect(() => { api.get("/tournaments/partners").then((r) => setPartners(r.data)).catch(() => {}); }, []);
  const pending = user && !user.approved && !STAFF_ROLES.includes(user.role);
  const submit = async () => {
    if (!pid && !pname.trim()) return toast.error("Escolha o parceiro ou digite o nome.");
    try {
      const r = await api.post(`/tournaments/${t.id}/register`, { category_id: cat.id, partner_user_id: pid, partner_name: pid ? "" : pname });
      setPaid(r.data); setPid(""); setPname(""); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (pending) return <p className="rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-800">Sua conta precisa ser aprovada para se inscrever.</p>;
  return (
    <div data-testid="tour-register" className="rounded-3xl border border-[#1B7A45]/20 bg-emerald-50/50 p-4">
      <p className="font-display text-lg font-black text-slate-900">Inscrever minha dupla · {cat.name}</p>
      <p className="text-xs text-slate-500">Você ({user?.name}) + parceiro. Valor por dupla: <strong>{brl(t.price)}</strong></p>
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto_1fr]">
        <select data-testid="tour-partner-select" value={pid} onChange={(e) => setPid(e.target.value)} className="w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
          <option value="">Escolher parceiro com login...</option>
          {partners.map((p) => <option key={p.user_id} value={p.user_id}>{p.name}</option>)}
        </select>
        <span className="self-center text-center text-xs text-slate-400">ou</span>
        <Input data-testid="tour-partner-name" disabled={!!pid} placeholder="Digite o nome do parceiro" value={pname} onChange={(e) => setPname(e.target.value)} className="rounded-xl bg-white" />
      </div>
      <Button data-testid="tour-register-btn" onClick={submit} className="mt-3 w-full rounded-full bg-[#1B7A45] text-white hover:bg-[#156337]">Inscrever e pagar com PIX</Button>
      <Dialog open={!!paid} onOpenChange={(o) => !o && setPaid(null)}>
        <DialogContent data-testid="tour-pix-dialog" aria-describedby={undefined} className="max-w-md rounded-3xl">
          <DialogHeader><DialogTitle className="font-display text-xl">Pague a inscrição pelo PIX</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Valor: <strong className="text-[#1B7A45]">{brl(paid?.price || 0)}</strong>. Após o pagamento, o administrador confirma sua dupla.</p>
          {pix.key ? (
            <div className="flex items-center justify-between gap-2 rounded-2xl bg-[#0b1610] p-4 text-white">
              <div className="min-w-0"><p className="text-xs text-emerald-300">PIX {pix.holder ? `· ${pix.holder}` : ""}</p><p className="break-all font-display font-black">{pix.key}</p></div>
              <button data-testid="tour-pix-copy" onClick={() => { navigator.clipboard?.writeText(pix.key); toast.success("Chave PIX copiada!"); }} className="rounded-lg bg-white/10 p-2 hover:bg-white/20"><Copy className="h-4 w-4" /></button>
            </div>
          ) : <p className="text-sm text-slate-500">Chave PIX ainda não cadastrada. Fale com o administrador.</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function TournamentPage() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [tid, setTid] = useState(null);
  const [data, setData] = useState(null);
  const [catId, setCatId] = useState(null);
  const [ranking, setRanking] = useState([]);
  const [pix, setPix] = useState({});

  useEffect(() => {
    api.get("/tournaments").then((r) => { setList(r.data); const cur = r.data.find((x) => x.status !== "finalizado") || r.data[0]; if (cur) setTid(cur.id); }).catch(() => {});
    api.get("/settings").then((r) => setPix({ key: r.data.bar_pix_key, holder: r.data.bar_pix_holder })).catch(() => {});
  }, []);
  const load = useCallback(() => {
    if (tid) api.get(`/tournaments/${tid}`).then((r) => { setData(r.data); setCatId((c) => c || r.data.categories[0]?.id); }).catch(() => {});
    api.get("/tournaments/ranking").then((r) => setRanking(r.data)).catch(() => {});
  }, [tid]);
  useEffect(() => {
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, [load]);

  const cat = data?.categories.find((c) => c.id === catId);
  const mine = data ? data.teams.filter((x) => x.registered_by === user?.user_id && x.category_id === catId) : [];
  const confirmed = data ? data.teams.filter((x) => x.status === "confirmada" && x.category_id === catId) : [];

  return (
    <div className="min-h-screen bg-[#F4F1EA]">
      <header className="relative overflow-hidden bg-[#0b1610] px-5 pb-10 pt-6 text-center text-white">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0b1610] via-[#14263f] to-[#0b1610]" />
        <div className="relative mx-auto max-w-4xl">
          <Link to="/" data-testid="tour-back" className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white"><ArrowLeft className="h-4 w-4" /> Menu</Link>
          <Trophy className="mx-auto mt-4 h-12 w-12 text-amber-400" />
          <h1 data-testid="tour-title" className="mt-2 font-display text-4xl font-black tracking-tight sm:text-5xl">{data?.name || "Torneio"}</h1>
          {data && <p className="mt-2 inline-flex flex-wrap items-center justify-center gap-3 text-sm text-white/70"><span className="rounded-full bg-white/10 px-3 py-1 font-semibold">{STATUS[data.status]}</span>{data.date && <span className="inline-flex items-center gap-1"><CalendarDays className="h-4 w-4" /> {data.date.split("-").reverse().join("/")}</span>}<span>Inscrição: {brl(data.price)} por dupla</span></p>}
          {data?.description && <p className="mx-auto mt-3 max-w-xl whitespace-pre-line text-sm text-white/80">{data.description}</p>}
          {list.length > 1 && (
            <select data-testid="tour-select" value={tid || ""} onChange={(e) => { setTid(e.target.value); setCatId(null); setData(null); }} className="mt-4 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm text-white">
              {list.map((x) => <option key={x.id} value={x.id} className="text-slate-900">{x.name}</option>)}
            </select>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-8 px-5 pb-28 pt-8">
        {!data && list.length === 0 && <p data-testid="tour-empty" className="rounded-3xl border border-dashed border-slate-300 bg-white py-12 text-center text-slate-400">Nenhum torneio cadastrado no momento.</p>}
        {data && (
          <>
            <div className="flex flex-wrap gap-2">
              {data.categories.map((c) => (
                <button key={c.id} data-testid={`tour-cat-${c.id}`} onClick={() => setCatId(c.id)} className={`rounded-full px-4 py-2 text-sm font-bold transition-colors ${c.id === catId ? "bg-[#1B7A45] text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}>{c.name} <span className="opacity-70">({c.teams_confirmed})</span></button>
              ))}
            </div>
            {cat && data.status === "inscricoes" && !cat.format && <RegisterBox t={data} cat={cat} onDone={load} pix={pix} />}
            {cat && data.status === "inscricoes" && cat.format && <p className="rounded-2xl bg-white px-4 py-3 text-sm text-slate-500">Inscrições desta categoria encerradas — sorteio realizado.</p>}
            {mine.length > 0 && (
              <div data-testid="tour-my-teams" className="space-y-1.5">
                {mine.map((x) => <p key={x.id} className="rounded-xl bg-white px-4 py-2 text-sm text-slate-700">Sua dupla: <strong>{x.player1.name} & {x.player2.name}</strong> · <span className={x.status === "confirmada" ? "font-bold text-[#1B7A45]" : "font-bold text-amber-600"}>{x.status === "confirmada" ? "confirmada" : "aguardando confirmação do PIX"}</span></p>)}
              </div>
            )}
            {cat && <TournamentBoard data={data} cat={cat} />}
            <section>
              <div className="flex items-center gap-2"><Users className="h-5 w-5 text-[#1B7A45]" /><h2 className="font-display text-xl font-extrabold text-slate-900">Duplas confirmadas ({confirmed.length})</h2></div>
              <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {confirmed.map((x) => <p key={x.id} data-testid={`tour-team-${x.id}`} className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700">{x.player1.name} & {x.player2.name}</p>)}
              </div>
            </section>
          </>
        )}
        {ranking.length > 0 && (
          <section data-testid="tour-ranking">
            <div className="flex items-center gap-2"><Crown className="h-6 w-6 text-amber-500" /><h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">Ranking dos Torneios</h2></div>
            <p className="mt-1 text-sm text-slate-500">Pontos somados por colocação em todos os torneios.</p>
            <Podium ranking={ranking} testPrefix="tour-podium" />
            <RankList ranking={ranking} testPrefix="tour-rank" />
          </section>
        )}
      </main>
      <FloatingMenuButton />
    </div>
  );
}
